import { resamplePolyline, polylineLength } from '../../../../ft3d/engine/ft3d-move-core.js';

const MOVEMENT_EPS = 1e-9; // FT_MOVEMENT_PCT_EPS, movement_pool.php

/** ft_movement_pct_store twin: the DECIMAL(14,10) column rule, so local truth equals SQL readback. */
export function storeMovementPct(pct: number): number {
  return Math.round(Math.max(0, pct) * 1e10) / 1e10;
}

export interface BankedWalkPrice {
  remainingPct: number;
  speedCostFt: number;
  steps: number;
  apsSpent: number;
  /** Paid 1 ft samples whose per-foot multiplier was 2. */
  difficult_steps?: number;
  /** false only in partial mode: the pool + AP ran dry before the last sample (M1d). */
  complete: boolean;
  /** Samples paid (= the index of the stop on the 1 ft resample; 0 = nothing affordable). */
  stopIndex: number;
  /** The last paid resampled point — the legacy per-foot loop's `$current_x/$current_z` at its break. */
  stopPoint: readonly [number, number];
}

/** Pure movement-pool accounting (ft_movement_pay_units twin over the shared 1 ft resampler).
 * The caller must establish ordinary walking, combat, clear terrain and no reactions.
 * M1b calls it with availableAps 0; M1c with the creature's `aps`. A walk the pool + AP cannot pay
 * whole returns null — unless `partial` is set (M1d), in which case the price stops at the LAST PAID
 * SAMPLE exactly as move_encounter_creature_logic() does: each resampled foot is paid all-or-nothing
 * (dry-run first, so a failed foot leaves the pool and AP untouched) and the walk breaks there with
 * `complete: false`. Round the result with storeMovementPct before committing.
 */
export function priceBankedWalk(
  points: ReadonlyArray<readonly [number, number]>,
  movementPct: number,
  baseSpeedFt: number,
  availableAps = 0,
  options: { partial?: boolean; multiplier?: (point: readonly [number, number]) => 1 | 2 | 4 } = {},
): BankedWalkPrice | null {
  if (!Number.isSafeInteger(availableAps) || availableAps < 0
    || !Number.isFinite(movementPct) || movementPct < 0
    || !Number.isFinite(baseSpeedFt) || baseSpeedFt <= 0
    || points.length < 2 || points.length > 512
    || points.some(p => !p.every(Number.isFinite))) return null;
  const route: Array<[number, number]> = points.map(p => [p[0], p[1]]);
  // Bound preparation work before invoking the existing per-foot resampler.
  if (polylineLength(route) > 10000) return null;
  const path = resamplePolyline(route, 1);
  if (path.length < 2) return null;
  const unitCost = 1 / baseSpeedFt;
  // ft_movement_unit_cost_pct twin: a denormal speed makes the reciprocal INF, which PHP refuses for any positive
  // units (pay_units returns ok=false) — never price with it (Codex M1d review B).
  if (!Number.isFinite(unitCost)) return null;
  let remaining = movementPct, speedCostFt = 0, apsSpent = 0, difficultSteps = 0;
  let complete = true, stopIndex = path.length - 1;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!;
    const spatial = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const multiplier = options.multiplier?.(b) ?? 1;
    // MV8: 4 = a HIDDEN mover (×2 stance) on a ×2 foot
    if (multiplier !== 1 && multiplier !== 2 && multiplier !== 4) return null;
    const units = spatial * multiplier;
    const whole = Math.floor(units + MOVEMENT_EPS);
    const fraction = Math.max(0, units - whole);
    // ft_movement_pay_units is all-or-nothing per step: dry-run the step on copies, commit only when it pays.
    let stepRemaining = remaining, stepAps = apsSpent, affordable = true;
    for (let unit = 0; unit <= whole; unit++) {
      const cost = unit < whole ? unitCost : unitCost * fraction;
      if (cost <= MOVEMENT_EPS) continue;
      if (stepRemaining + MOVEMENT_EPS < cost) {
        if (stepAps >= availableAps) { affordable = false; break; }
        stepAps++;
        stepRemaining += 1;
      }
      stepRemaining -= cost;
    }
    if (!affordable) {
      if (!options.partial) return null;
      complete = false; stopIndex = i - 1;
      break;
    }
    remaining = stepRemaining; apsSpent = stepAps;
    if (remaining < MOVEMENT_EPS) remaining = 0;
    speedCostFt += units;
    if (multiplier === 2) difficultSteps++;
  }
  const stop = path[stopIndex]!;
  return { remainingPct: remaining, speedCostFt, steps: stopIndex, apsSpent,
    ...(options.multiplier ? { difficult_steps: difficultSteps } : {}),
    complete, stopIndex, stopPoint: [stop[0], stop[1]] };
}
